import fetcher from '@lib/fetcher'
import { getAllCollectionsQuery, getCollectionId, getCollectionProductsQuery } from '@lib/queries'
import { getCollectionProductsOnlyQuery } from '@lib/queries/get-collection-products-only-query'
import type {
	Collection,
	CollectionProduct,
	FilterValue,
	Filters,
	PageInfo,
	SortByOptions
} from '@lib/types'
import type { SamplesFilterKeyType } from '@lib/types/raw'
import {
	normalizeCollection,
	normalizeCollectionProductSamples,
	normalizeCollectionProducts,
	normalizeCollections,
	normalizeFilters,
	normalizeProductConnection
} from '@lib/utils/normalizers'

/**
 * Get all the prducts by slug.
 * @returns array of all the collections
 */
export const getAllCollections = async () => {
	const allCollections = await fetcher({ query: getAllCollectionsQuery })
	return normalizeCollections(allCollections.collections)
}

export const getAllCollectionProducts = async (handle: string) => {
	// Get the collection id to use in the next query
	const collectionId = await fetcher({
		variables: { handle },
		query: getCollectionId
	})

	// if collection id is found, get the products and the collection details
	if (collectionId?.collectionByHandle?.id) {
		const allCollectionProducts = await fetcher({
			variables: { categoryId: collectionId?.collectionByHandle?.id },
			query: getCollectionProductsQuery
		})

		return {
			...allCollectionProducts.node,
			products: normalizeProductConnection(allCollectionProducts.node.products)
		}
	}
	return null
}

export type GetCollectionProductsPerPageOptions = {
	endCursor?: null | string
	sortBy?: SortByOptions['sortBy']
	reverse?: boolean
	filters?: FilterValue['input'][]
	productsPerPage?: number
	onlyFullSizeProducts?: boolean
	onlyWithStock?: boolean
}

export type GetCollectionProductsPerPageReturn = {
	collection: Collection | null
	products: CollectionProduct[]
	pageInfo: PageInfo
	filters: Filters
}

export const PRODUCTS_PER_PAGE = 20

export const getCollectionProductsPerPage = async (
	handle: string,
	options: GetCollectionProductsPerPageOptions = {
		endCursor: null,
		sortBy: 'COLLECTION_DEFAULT',
		reverse: false,
		filters: [],
		productsPerPage: PRODUCTS_PER_PAGE,
		onlyFullSizeProducts: true,
		onlyWithStock: true
	}
): Promise<GetCollectionProductsPerPageReturn | null> => {
	const { collection } = await fetcher({
		variables: {
			collectionHandle: handle,
			first: options.productsPerPage,
			after: options.endCursor,
			sortKey: options.sortBy,
			reverse: options.reverse,
			filters: options.filters
		},
		query: getCollectionProductsQuery
	})

	let products =
		collection &&
		collection.products &&
		collection.products.edges &&
		collection.products.edges.length > 0
			? normalizeCollectionProducts(collection.products.edges, {
					onlyFullSizeProducts: options.onlyFullSizeProducts ?? true,
					onlyWithStock: options.onlyWithStock ?? true
				})
			: []

	// Manual sorting, since Shopify sorting uses minVariantPrice which is often 0
	if (options.sortBy === 'PRICE') {
		products = products.sort(
			(a, b) => Number(a.price.amount) + (options.reverse ? 1 : -1) * Number(b.price.amount)
		)
	}
	if (!collection) return null

	return {
		collection: collection ? normalizeCollection(collection) : null,
		filters: collection ? normalizeFilters(collection.products.filters) : [],
		products,
		pageInfo: collection ? collection.products.pageInfo : null
	}
}

export const getProductSamplesFromCollection = async (
	handle: string,
	filter: SamplesFilterKeyType = null,
	stockTolerance = 0
) => {
	const { collection } = await fetcher({
		variables: {
			collectionHandle: handle
		},
		query: getCollectionProductsOnlyQuery
	})

	const products =
		collection &&
		collection.products &&
		collection.products.edges &&
		collection.products.edges.length > 0
			? normalizeCollectionProductSamples(collection.products.edges, filter, stockTolerance)
			: []

	return products
}
